import { isNullish } from '../../shared/utils';
import {
  ArmFixedTerm,
  AutomatedUWSystem,
  BankruptcyOutcome,
  BankruptcySeasoning,
  BankruptcyType,
  PricingBorrower,
  BuyDown,
  Citizenship,
  DesiredLockPeriod,
  DesiredType,
  EscrowWaiver,
  FhaCaseAssigned,
  HousingEventSeasoning,
  HousingEventType,
  IncomeVerificationType,
  LoanPurpose,
  LoanTerm,
  MIProvider,
  MortgageType,
  NumberOfUnits,
  OccupancyType,
  PrepaymentPenalty,
  ProductType,
  PropertyType,
  SecondMortgage,
  UsdaCaseAssigned,
  VaFundingFeeType,
  CoverageType,
  CoveragePercentage,
  MiMethod,
  PricingResult,
  FrequencyType,
  HousingExpenseType,
  AdditionalHousingExpenseTypes,
  AdditionalHousingExpenseFieldMap,
  HousingExpenseFormValue,
  HousingExpenseCalcMethod,
  createDefaultHousingExpenseFormValue,
  AdditionalHousingExpenseField,
  DisbursementSchedule,
  Month,
  PrepaymentStructure,
  SectionOfAct,
} from './pricing.models';
import { PmiType, ScenarioCriteriaModel } from './scenario-pricing.models';

export class PricingInputFormModel {
  /* Basic Info */
  guid?: string;
  loanPurpose: LoanPurpose = LoanPurpose.Purchase;
  borrowerLastName: string;
  mortgageTypes: MortgageType[] = [MortgageType.Conforming, MortgageType.None];
  loanTerms: LoanTerm[] = [LoanTerm.Year30, LoanTerm.Year15];
  armFixedTerms: ArmFixedTerm[] = [ArmFixedTerm.Year7];
  purchasePrice?: number = null;
  appraisedValue?: number = null;
  desiredLockPeriod: DesiredLockPeriod = DesiredLockPeriod.Days30;
  downPaymentAmount?: number = null;
  downPaymentPercent?: number = null;
  cashOutAmount?: number = null;
  baseLoanAmount?: number = null;
  baseLtv?: number = null;
  fico?: number = null;
  dti = 0;
  monthlyQualifyingIncome?: number;
  communityLending?: boolean = false;
  street?: string;
  city?: string;
  zip: string;
  state: string;
  county: string;
  occupancyType: OccupancyType = OccupancyType.PrimaryResidence;
  propertyType: PropertyType = PropertyType.SingleFamily;
  noOfUnits: NumberOfUnits = NumberOfUnits.OneUnit;
  noOfStories = 1;
  noOfScenarios?: number = null;
  scenarioNameAiSuffix: string;
  scenarioName?: string;
  /* end Basic Info */
  /* Advanced */
  desiredType: DesiredType = DesiredType.Price;
  desiredPrice = 100;
  desiredRate: number = null;
  interestOnly: boolean = false;
  waiveEscrows: boolean = false;
  buyDown: BuyDown = BuyDown.None;
  firstTimeHomeBuyer: boolean = false;
  secondMortgage: SecondMortgage = SecondMortgage.None;
  heLoanAmount?: number = null;
  helocLineAmount?: number = null;
  helocDrawnAmount?: number = null;
  nonOccupantCoBorrower: boolean = false;
  monthsOfReserve = 12;
  citizenship: Citizenship = Citizenship.UsCitizen;
  automatedUnderwritingSystem: AutomatedUWSystem = AutomatedUWSystem.NotSpecified;
  /* end Advanced */
  /* Housing Expenses */
  propertyTax: HousingExpenseFormValue = createDefaultHousingExpenseFormValue(HousingExpenseType.PropertyTaxes);
  homeownersInsurance: HousingExpenseFormValue = createDefaultHousingExpenseFormValue(HousingExpenseType.HomeownersInsurance);
  floodInsurance: HousingExpenseFormValue = createDefaultHousingExpenseFormValue(HousingExpenseType.FloodInsurance);
  hoaDues: HousingExpenseFormValue = createDefaultHousingExpenseFormValue(HousingExpenseType.Hoa);
  additionalHousingExpenses?: HousingExpenseFormValue[];

  /* end Housing Expenses */
  /* Mortgage Insurance */
  usdaCaseAssigned: UsdaCaseAssigned = null;
  borrowerPaysMiConv: boolean;
  borrowerPaysMiGovt: boolean;
  isFinanceEntireAmountConv: boolean;
  isFinanceEntireAmountGovt: boolean;
  fhaCaseAssigned?: FhaCaseAssigned = null;
  vaFundingFeeType: VaFundingFeeType = null;
  exemptFromFundingFee: boolean;
  upfrontPmiMipFfGFeePercentConv?: number = null;
  upfrontPmiMipFfGFeeAmountConv?: number = null;
  upfrontPmiMipFfGFeePercentGovt?: number = null;
  upfrontPmiMipFfGFeeAmountGovt?: number = null;
  monthlyPmiMipFfGFeePercentConv?: number = null;
  monthlyPmiMipFfGFeeAmountConv?: number = null;
  monthlyPmiMipFfGFeePercentGovt?: number = null;
  monthlyPmiMipFfGFeeAmountGovt?: number = null;
  totalLoanAmountConv?: number = null;
  totalLoanAmountGovt?: number = null;
  upfrontPmiMipFfGFeeAmountFinancedConv?: number = null;
  upfrontPmiMipFfGFeeAmountCashConv?: number = null;
  upfrontPmiMipFfGFeeAmountFinancedGovt?: number = null;
  upfrontPmiMipFfGFeeAmountCashGovt?: number = null;
  calculateTotalLoanAmount?: boolean = false;
  pmiType?: PmiType | string = PmiType.BorrowerSingleNonRefundable;
  miMethod?: MiMethod | number = MiMethod.BestXMI;

  /* end Mortgage Insurance */
  /* Product Type(s) */
  productTypes: ProductType[] = [ProductType.StandardProducts];
  timesMortPmntLateBy30DaysInLast1To12Mos = 0;
  timesMortPmntLateBy60DaysInLast1To12Mos = 0;
  timesMortPmntLateBy90DaysInLast1To12Mos = 0;
  timesMortPmntLateBy120DaysInLast1To12Mos = 0;
  timesMortPmntLateBy30DaysInLast13To24Mos = 0;
  timesMortPmntLateBy60DaysInLast13To24Mos = 0;
  timesMortPmntLateBy90DaysInLast13To24Mos = 0;
  timesMortPmntLateBy120DaysInLast13To24Mos = 0;
  incomeVerificationType: IncomeVerificationType = IncomeVerificationType.FullDoc;
  dscr = 0;
  prepaymentPenalty: PrepaymentPenalty = PrepaymentPenalty.None;
  selfEmployed: boolean = false;
  bankruptcyType: BankruptcyType = BankruptcyType.None;
  bankruptcyOutcome: BankruptcyOutcome = BankruptcyOutcome.NotApplicable;
  bankruptcySeasoning: BankruptcySeasoning = BankruptcySeasoning.NotApplicable;
  housingEventType: HousingEventType = HousingEventType.None;
  housingEventSeasoning: HousingEventSeasoning = HousingEventSeasoning.NotApplicable;
  mhAdvantage?: boolean = false;
  communityAffordableSecond?: boolean = false;
  noOfFinancedProperties?: number = 1;
  miProviders: MIProvider[] = [
    MIProvider.ArchMI,
    MIProvider.BestX,
    MIProvider.Enact,
    MIProvider.Essent,
    MIProvider.MGIC,
    MIProvider.NationalMI,
    MIProvider.Radian,
  ];
  htiWithoutMi: number = this.dti;
  dtiWithoutMi: number = this.dti;
  addCoBorrower: boolean = false;
  borrowers?: PricingBorrower[] = [
    {
      isPrimary: true,
      fico: null,
      selfEmployed: false,
      hadForeclosure: false,
      hadBankruptcy: false,
    },
  ];

  coverageType: CoverageType = CoverageType.Standard;
  coveragePercentage: CoveragePercentage | number;
  scenarioCriteriaGUID?: string;
  uniqueProperty?: boolean = false;
  debtConsolidation?: boolean = false;
  prepaymentStructure?: PrepaymentStructure | number;
  borrowerFirstName?: string;
  estimatedClosingDate?: string;
  estimatedFundingDate?: string;
  firstPaymentDate?: string;
  aggregateAdjustment?: number;
  fhaSectionOfAct?: SectionOfAct;
  fhaPrevEndorsementDate?: string;
  isPud?: boolean = false;
  cardsAndOtherDebtPaidOff?: number = null;
  earnestMoneyDeposit?: number = null;
  mortgageLoansToBePaid?: number = null;
  lumpSumSellerCredit?: number = null;
  escrowWaiver?: EscrowWaiver;

  constructor(scenarioCriteria?: ScenarioCriteriaModel, savedScenario?: PricingResult) {
    if (scenarioCriteria) {
      /* Basic Info */
      this.loanPurpose = parseInt(scenarioCriteria.loanPurpose as string);
      this.borrowerLastName = scenarioCriteria.borrowerLastName ? scenarioCriteria.borrowerLastName : '';

      this.mortgageTypes = scenarioCriteria.mortgageTypes.map((v) => {
        return parseInt(v);
      });

      this.loanTerms = scenarioCriteria.loanTerms.map((v) => {
        return parseInt(v);
      });

      this.armFixedTerms = scenarioCriteria.armFixedTerms.map((v) => {
        return parseInt(v);
      });

      this.purchasePrice = scenarioCriteria.purchasePrice;
      if (scenarioCriteria.loanPurpose !== LoanPurpose.Purchase) {
        this.purchasePrice = scenarioCriteria.estimatedValue ?? scenarioCriteria.purchasePrice;
      }

      this.appraisedValue = scenarioCriteria.appraisedValue;
      this.desiredLockPeriod = scenarioCriteria.desiredLockPeriod;
      this.downPaymentAmount = scenarioCriteria.downPaymentAmount;
      this.downPaymentPercent = scenarioCriteria.downPaymentPercent;
      this.cashOutAmount = scenarioCriteria.cashOutAmount;
      this.baseLoanAmount = scenarioCriteria.baseLoanAmount;
      this.baseLtv = scenarioCriteria.baseLtv;
      this.fico = scenarioCriteria.fico;
      this.dti = scenarioCriteria.dti;
      this.monthlyQualifyingIncome = scenarioCriteria.monthlyQualifyingIncome;
      this.zip = scenarioCriteria.zip;
      this.state = scenarioCriteria.state;
      this.county = scenarioCriteria.county;
      this.occupancyType = parseInt(scenarioCriteria.occupancyType as string);
      this.propertyType = parseInt(scenarioCriteria.propertyType as string);
      this.noOfUnits = parseInt(scenarioCriteria.noOfUnits as string);
      this.noOfStories = scenarioCriteria.noOfStories;
      /* end Basic Info */
      /* Advanced */
      this.desiredType = scenarioCriteria.desiredPrice ? DesiredType.Price : DesiredType.Rate;
      this.desiredPrice = scenarioCriteria.desiredPrice;
      this.desiredRate = scenarioCriteria.desiredRate;
      this.interestOnly = isNullish(scenarioCriteria.interestOnly) ? false : scenarioCriteria.interestOnly;
      this.waiveEscrows = isNullish(scenarioCriteria.waiveEscrows) ? false : scenarioCriteria.waiveEscrows;
      this.buyDown = parseInt(scenarioCriteria.buyDown as string);
      this.firstTimeHomeBuyer = isNullish(scenarioCriteria.firstTimeHomeBuyer) ? false : scenarioCriteria?.firstTimeHomeBuyer;
      this.secondMortgage = parseInt(scenarioCriteria.secondMortgage as string);
      this.heLoanAmount = scenarioCriteria.heLoanAmount;
      this.helocLineAmount = scenarioCriteria.helocLineAmount;
      this.helocDrawnAmount = scenarioCriteria.helocDrawnAmount;
      this.nonOccupantCoBorrower = isNullish(scenarioCriteria.nonOccupantCoBorrower) ? false : scenarioCriteria.nonOccupantCoBorrower;
      this.monthsOfReserve = scenarioCriteria.monthsOfReserve;
      this.citizenship = parseInt(scenarioCriteria.citizenship as string);
      this.automatedUnderwritingSystem = parseInt(scenarioCriteria.automatedUnderwritingSystem as string);
      /* end Advanced */
      /* Housing Expenses */
      // Handle both API shape (housingExpenses[]) and form-persist shape (grouped properties from getRawValue)
      const sc = scenarioCriteria as any;
      if (sc.propertyTax && typeof sc.propertyTax === 'object' && 'amount' in sc.propertyTax) {
        // Form-persist shape: grouped properties already in HousingExpenseFormValue format
        this.propertyTax = { ...createDefaultHousingExpenseFormValue(HousingExpenseType.PropertyTaxes), ...sc.propertyTax };
        this.homeownersInsurance = { ...createDefaultHousingExpenseFormValue(HousingExpenseType.HomeownersInsurance), ...sc.homeownersInsurance };
        this.floodInsurance = { ...createDefaultHousingExpenseFormValue(HousingExpenseType.FloodInsurance), ...(sc.floodInsurance ?? {}) };
        this.hoaDues = { ...createDefaultHousingExpenseFormValue(HousingExpenseType.Hoa), ...sc.hoaDues };
        this.additionalHousingExpenses = sc.additionalHousingExpenses ?? [];
      } else {
        // API shape: split housingExpenses[] into grouped form values
        this.propertyTax = PricingInputFormModel._mapHousingExpenseToFormValue(
          scenarioCriteria.housingExpenses?.find((hx) => hx.type === HousingExpenseType.PropertyTaxes),
          HousingExpenseType.PropertyTaxes,
        );
        this.homeownersInsurance = PricingInputFormModel._mapHousingExpenseToFormValue(
          scenarioCriteria.housingExpenses?.find((hx) => hx.type === HousingExpenseType.HomeownersInsurance),
          HousingExpenseType.HomeownersInsurance,
        );
        this.floodInsurance = PricingInputFormModel._mapHousingExpenseToFormValue(
          scenarioCriteria.housingExpenses?.find((hx) => hx.type === HousingExpenseType.FloodInsurance),
          HousingExpenseType.FloodInsurance,
        );
        this.hoaDues = PricingInputFormModel._mapHousingExpenseToFormValue(
          scenarioCriteria.housingExpenses?.find((hx) => hx.type === HousingExpenseType.Hoa),
          HousingExpenseType.Hoa,
        );
        this.additionalHousingExpenses = scenarioCriteria.housingExpenses
          ?.filter((hx) => AdditionalHousingExpenseTypes.has(hx.type))
          ?.map((ahx) => PricingInputFormModel._mapHousingExpenseToFormValue(ahx, ahx.type, AdditionalHousingExpenseFieldMap[ahx.type]));
      }
      /* end Housing Expenses */
      /* Mortgage Insurance */
      this.usdaCaseAssigned = scenarioCriteria.usdaCaseAssigned;
      this.borrowerPaysMiConv = scenarioCriteria.borrowerPaysMiConv;
      this.isFinanceEntireAmountConv = scenarioCriteria.isFinanceEntireAmountConv;
      this.isFinanceEntireAmountGovt = scenarioCriteria.isFinanceEntireAmountGovt;
      this.fhaCaseAssigned = scenarioCriteria.fhaCaseAssigned as FhaCaseAssigned;
      this.vaFundingFeeType = parseInt(scenarioCriteria.vaFundingFeeType as string);
      this.exemptFromFundingFee = scenarioCriteria.exemptFromFundingFee;
      this.upfrontPmiMipFfGFeePercentConv =
        (scenarioCriteria.upfrontPmiMipFfGFeePercentConv ?? !!savedScenario?.upfrontPremiumRate) ? savedScenario?.upfrontPremiumRate : null;
      this.upfrontPmiMipFfGFeeAmountConv = scenarioCriteria.upfrontPmiMipFfGFeeAmountConv;
      this.upfrontPmiMipFfGFeePercentGovt = scenarioCriteria.upfrontPmiMipFfGFeePercentGovt;
      this.upfrontPmiMipFfGFeeAmountGovt = scenarioCriteria.upfrontPmiMipFfGFeeAmountGovt;
      this.monthlyPmiMipFfGFeeAmountConv = scenarioCriteria.monthlyPmiMipFfGFeeAmountConv;
      this.monthlyPmiMipFfGFeePercentConv =
        (scenarioCriteria.monthlyPmiMipFfGFeePercentConv ?? !!savedScenario?.monthlyPremiumRate) ? savedScenario?.monthlyPremiumRate : null;
      this.monthlyPmiMipFfGFeeAmountGovt = scenarioCriteria.monthlyPmiMipFfGFeeAmountGovt;
      this.monthlyPmiMipFfGFeePercentGovt = scenarioCriteria.monthlyPmiMipFfGFeePercentGovt;
      this.totalLoanAmountConv = scenarioCriteria.totalLoanAmountConv;
      this.totalLoanAmountGovt = scenarioCriteria.totalLoanAmountGovt;
      this.upfrontPmiMipFfGFeeAmountFinancedConv = scenarioCriteria.upfrontPmiMipFfGFeeAmountFinancedConv;
      this.upfrontPmiMipFfGFeeAmountCashConv = scenarioCriteria.upfrontPmiMipFfGFeeAmountCashConv;
      this.upfrontPmiMipFfGFeeAmountFinancedGovt = scenarioCriteria.upfrontPmiMipFfGFeeAmountFinancedGovt;
      this.pmiType = savedScenario?.pmiType ?? PmiType.BorrowerSingleNonRefundable;
      this.miMethod = !scenarioCriteria.calculateTotalLoanAmount ? MiMethod.Manual : MiMethod.BestXMI;
      /* end Mortgage Insurance */
      /* Product Type(s) */
      if (scenarioCriteria.productTypes) {
        this.productTypes = [];
        scenarioCriteria.productTypes.forEach((pt) => {
          const productTypeValue = parseInt(pt as string);
          this.productTypes.push(productTypeValue as ProductType);
        });
      }
      this.timesMortPmntLateBy30DaysInLast1To12Mos = scenarioCriteria.timesMortPmntLateBy30DaysInLast1To12Mos;
      this.timesMortPmntLateBy30DaysInLast13To24Mos = scenarioCriteria.timesMortPmntLateBy30DaysInLast13To24Mos;
      this.timesMortPmntLateBy60DaysInLast1To12Mos = scenarioCriteria.timesMortPmntLateBy60DaysInLast1To12Mos;
      this.timesMortPmntLateBy60DaysInLast13To24Mos = scenarioCriteria.timesMortPmntLateBy60DaysInLast13To24Mos;
      this.timesMortPmntLateBy90DaysInLast1To12Mos = scenarioCriteria.timesMortPmntLateBy90DaysInLast1To12Mos;
      this.timesMortPmntLateBy90DaysInLast13To24Mos = scenarioCriteria.timesMortPmntLateBy90DaysInLast13To24Mos;
      this.timesMortPmntLateBy120DaysInLast1To12Mos = scenarioCriteria.timesMortPmntLateBy120DaysInLast1To12Mos;
      this.timesMortPmntLateBy120DaysInLast13To24Mos = scenarioCriteria.timesMortPmntLateBy120DaysInLast13To24Mos;
      this.incomeVerificationType = isNullish(scenarioCriteria.incomeVerificationType)
        ? null
        : parseInt(scenarioCriteria.incomeVerificationType as string);
      this.dscr = parseInt(scenarioCriteria.dscr as string);
      this.prepaymentPenalty = isNullish(scenarioCriteria.prepaymentPenalty) ? null : parseInt(scenarioCriteria.prepaymentPenalty as string);
      this.selfEmployed = scenarioCriteria.selfEmployed ?? false;
      this.bankruptcyType = isNullish(scenarioCriteria.bankruptcyType) ? null : parseInt(scenarioCriteria.bankruptcyType as string);
      this.bankruptcyOutcome = isNullish(scenarioCriteria.bankruptcyOutcome) ? null : parseInt(scenarioCriteria.bankruptcyOutcome as string);
      this.bankruptcySeasoning = isNullish(scenarioCriteria.bankruptcySeasoning) ? null : parseInt(scenarioCriteria.bankruptcySeasoning as string);
      this.housingEventType = isNullish(scenarioCriteria.housingEventType) ? null : parseInt(scenarioCriteria.housingEventType as string);
      this.housingEventSeasoning = isNullish(scenarioCriteria.housingEventSeasoning)
        ? null
        : parseInt(scenarioCriteria.housingEventSeasoning as string);
      this.communityLending = isNullish(scenarioCriteria.communityLending) ? false : scenarioCriteria.communityLending;
      this.street = isNullish(scenarioCriteria.street) ? null : scenarioCriteria.street;
      this.city = isNullish(scenarioCriteria.city) ? null : scenarioCriteria.city;
      this.mhAdvantage = isNullish(scenarioCriteria.mhAdvantage) ? false : scenarioCriteria.mhAdvantage;
      this.communityAffordableSecond = isNullish(scenarioCriteria.communityAffordableSecond) ? false : scenarioCriteria.communityAffordableSecond;
      this.noOfFinancedProperties = isNullish(scenarioCriteria.noOfFinancedProperties) ? 1 : scenarioCriteria.noOfFinancedProperties;
      this.miProviders =
        isNullish(scenarioCriteria.miProviders) || scenarioCriteria.miProviders.length === 0 ? this.miProviders : scenarioCriteria.miProviders;
      this.htiWithoutMi = isNullish(scenarioCriteria.htiWithoutMi) ? this.dti : scenarioCriteria.htiWithoutMi;
      this.dtiWithoutMi = isNullish(scenarioCriteria.dtiWithoutMi) ? this.dti : scenarioCriteria.dtiWithoutMi;
      this.addCoBorrower = isNullish(scenarioCriteria.addCoBorrower) ? null : scenarioCriteria.addCoBorrower;
      this.coveragePercentage = isNullish(scenarioCriteria.coveragePercentage) ? null : scenarioCriteria.coveragePercentage;
      this.coverageType = isNullish(scenarioCriteria.coverageType) ? CoverageType.Standard : scenarioCriteria.coverageType;

      if (isNullish(scenarioCriteria.borrowers)) {
        this.borrowers = [...this.borrowers];
        this.borrowers[0].fico = this.fico;
      } else {
        this.borrowers = scenarioCriteria.borrowers;
      }
      this.uniqueProperty = scenarioCriteria.uniqueProperty ?? false;
      this.debtConsolidation = scenarioCriteria.debtConsolidation ?? false;
      this.prepaymentStructure = scenarioCriteria.prepaymentStructure ?? PrepaymentStructure.NotApplicable;
      this.borrowerFirstName = scenarioCriteria.borrowerFirstName || null;
      this.estimatedClosingDate = scenarioCriteria.estimatedClosingDate || null;
      this.estimatedFundingDate = scenarioCriteria.estimatedFundingDate || null;
      this.firstPaymentDate = scenarioCriteria.firstPaymentDate || null;
      this.aggregateAdjustment = scenarioCriteria.aggregateAdjustment ?? null;
      this.fhaSectionOfAct = scenarioCriteria.fhaSectionOfAct || null;
      this.fhaPrevEndorsementDate = scenarioCriteria.fhaPrevEndorsementDate || null;
      this.isPud = isNullish(scenarioCriteria.isPud) ? false : scenarioCriteria.isPud;
      this.cardsAndOtherDebtPaidOff = scenarioCriteria?.cardsAndOtherDebtPaidOff || null;
      this.earnestMoneyDeposit = scenarioCriteria?.earnestMoneyDeposit || null;
      this.mortgageLoansToBePaid = scenarioCriteria?.mortgageLoansToBePaid || null;
      this.lumpSumSellerCredit = scenarioCriteria?.lumpSumSellerCredit || null;
      /* end Product Type(s) */
    }
  }

  private static _mapHousingExpenseToFormValue(
    hx:
      | {
          amount?: number;
          calcMethod?: HousingExpenseCalcMethod;
          factor?: number;
          frequency?: FrequencyType;
          type?: HousingExpenseType;
          disbursementSchedule?: DisbursementSchedule;
          nextDisbursementMonth?: Month[];
          nextDisbursementYear?: number;
          escrowed?: boolean;
          monthlyAmount?: number;
          escrowMonths?: number;
          escrowsTotal?: number;
          prepaidsMonths?: number;
          prepaidsTotal?: number;
          field?: AdditionalHousingExpenseField;
        }
      | undefined,
    type: HousingExpenseType,
    field?: AdditionalHousingExpenseField,
  ): HousingExpenseFormValue {
    const base = createDefaultHousingExpenseFormValue(type);
    if (!hx) {
      return base;
    }
    return {
      ...base,
      amount: hx.amount ?? null,
      calcMethod: hx.calcMethod ?? null,
      factor: hx.factor ?? null,
      frequency: hx.frequency ?? FrequencyType.Annually,
      field: field ?? hx.field ?? null,
      disbursementSchedule: hx.disbursementSchedule ?? null,
      escrowed: hx.escrowed ?? false,
      nextDisbursementMonth: hx.nextDisbursementMonth ?? null,
      nextDisbursementYear: hx.nextDisbursementYear ?? null,
      monthlyAmount: hx.monthlyAmount ?? null,
      escrowMonths: hx.escrowMonths ?? null,
      escrowsTotal: hx.escrowsTotal ?? null,
      prepaidsMonths: hx.prepaidsMonths ?? null,
      prepaidsTotal: hx.prepaidsTotal ?? null,
    };
  }
}
